import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import ShopHome from "@/pages/ShopHome";
import ProductDetails from "@/pages/ProductDetails";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import StoreShop from "@/pages/reseller/StoreShop";
import ResellerDashboard from "@/pages/reseller/ResellerDashboard";
import DistributorDashboard from "@/pages/distributor/DistributorDashboard";
import StorePreview from "@/pages/StorePreview";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Checkout from "@/pages/Checkout";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import StoreLogin from "@/pages/reseller/StoreLogin";
import StoreRegister from "@/pages/reseller/StoreRegister";
import StoreAccount from "@/pages/reseller/StoreAccount";
import StoreContactUs from "@/pages/reseller/StoreContactUs";
import CustomerLogin from "./pages/customer/CustomerLogin";
import CustomerRegister from "./pages/customer/CustomerRegister";
import CustomerAccount from "./pages/customer/CustomerAccount";
import CustomerForgotPassword from "./pages/customer/CustomerForgotPassword";
import CustomerResetPassword from "./pages/customer/CustomerResetPassword";
import GuestOrderTracking from "./pages/customer/GuestOrderTracking";
import OrderVerify from "./pages/customer/OrderVerify";
import ScrollToTop from "./components/ScrollToTop";
import PublicContentPage from "./pages/PublicContentPage";
import InfinityLayout from "./pages/customer-infinity/InfinityLayout";
import InfinityLogin from "./pages/customer-infinity/InfinityLogin";
import InfinityHome from "./pages/customer-infinity/InfinityHome";
import InfinityShop from "./pages/customer-infinity/InfinityShop";
import InfinityProductDetails from "./pages/customer-infinity/InfinityProductDetails";
import InfinityCheckout from "./pages/customer-infinity/InfinityCheckout";
import InfinityOrders from "./pages/customer-infinity/InfinityOrders";
import InfinityAddresses from "./pages/customer-infinity/InfinityAddresses";
import { InfinityCustomerProvider } from "./context/InfinityCustomerContext";
import { homePathForRole, canEnter } from "./utils/roleHome";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// Add interceptor to include auth token
axios.interceptors.request.use((config) => {
  // Try to get token from localStorage for testing scenarios
  const token = localStorage.getItem('session_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
export const AuthContext = ({ children }) => {
  return children;
};

// Auth Callback Component - Handles OAuth redirect
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId
          });

          const user = response.data;
          // Store session token in localStorage for backup auth
          if (user.session_token) {
            localStorage.setItem('session_token', user.session_token);
          }

          // Clear the hash and redirect based on role
          window.history.replaceState(null, '', window.location.pathname);

          const home = homePathForRole(user.role);
          if (home) {
            navigate(home, { state: { user }, replace: true });
          } else {
            // Block customer from dashboard access via OAuth
            navigate('/login', { replace: true });
            // Ideally show toast, but difficult in this callback component structure without context
          }
        } catch (error) {
          console.error('Auth error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (loading || isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // requiredRole is one role or a list (the admin panel admits admin and ops).
  if (!canEnter(requiredRole, user?.role)) {
    // Redirect to appropriate dashboard based on role
    return <Navigate to={homePathForRole(user?.role) || '/login'} replace />;
  }

  return children;
};

import { isStoreSubdomain, getInfinityTouchSurface } from "./utils/storeUtils";
import { StoreLayout } from "./components/layout/StoreLayout";

// Returns true if the current hostname is a store subdomain
// e.g. "cenosid.edisplayroom.com" → true
// e.g. "edisplayroom.com" → false
function isStoreDomain() {
  return isStoreSubdomain();
}

// The Infinity Touch CUSTOMER site lives on its own domain. In dev/preview it's
// also reachable under /it-customer so it can be exercised without that host.
function isInfinityCustomerDomain() {
  return window.location.hostname === 'customer.infinitytouch.app';
}

// Shared child routes for the IT customer storefront (same under host-root and
// the dev /it-customer prefix).
function infinityChildRoutes() {
  return (
    <>
      <Route index element={<InfinityLogin />} />
      <Route path="login" element={<InfinityLogin />} />
      <Route path="home" element={<InfinityHome />} />
      <Route path="case" element={<Navigate to="home" replace />} />
      <Route path="shop" element={<InfinityShop />} />
      <Route path="product/:id" element={<InfinityProductDetails />} />
      <Route path="checkout" element={<InfinityCheckout />} />
      <Route path="orders" element={<InfinityOrders />} />
      <Route path="addresses" element={<InfinityAddresses />} />
    </>
  );
}

import StoreForgotPassword from "./pages/reseller/StoreForgotPassword";
import StoreResetPassword from "./pages/reseller/StoreResetPassword";

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check for session_id in hash BEFORE rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Infinity Touch customer site on its own domain → render at /.
  if (isInfinityCustomerDomain()) {
    return (
      <Routes>
        <Route path="/" element={<InfinityLayout />}>
          {infinityChildRoutes()}
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // On store subdomains (cenosid.edisplayroom.com), render store routes
  // directly at / instead of /store/:subdomain — subdomain comes from hostname.
  // StorePreview and siblings read subdomain via getSubdomainFromHostname() fallback.
  if (isStoreDomain()) {
    return (
      <Routes>
        <Route path="/" element={<StoreLayout />}>
          <Route index element={<StorePreview />} />
          <Route path="contact" element={<StoreContactUs />} />
          <Route path="shop" element={<StoreShop />} />
          <Route path="product/:id" element={<ProductDetails />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="login" element={<StoreLogin />} />
          <Route path="register" element={<StoreRegister />} />
          <Route path="account" element={<StoreAccount />} />
          <Route path="order/verify" element={<OrderVerify />} />
          <Route path="checkout/success" element={<CheckoutSuccess />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/reseller-program" element={<LandingPage />} />
      <Route path="/product/:id" element={<ProductDetails />} />
      <Route path="/products" element={<ShopHome />} />
      <Route path="/collections" element={<ShopHome />} /> {/* Reuse ShopHome for collections view for now */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredRole={["admin", "ops"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/distributor/*"
        element={
          <ProtectedRoute requiredRole="distributor">
            <DistributorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/*"
        element={
          <ProtectedRoute requiredRole="reseller">
            <ResellerDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Storefront Routes with Shared Layout */}
      {/* Storefront Routes with Shared Layout */}
      <Route path="/store/:subdomain" element={<StoreLayout />}>
        <Route index element={<StorePreview />} />
        <Route path="contact" element={<StoreContactUs />} />
        <Route path="shop" element={<StoreShop />} />
        <Route path="product/:id" element={<ProductDetails />} />
        <Route path="checkout" element={<Checkout />} />
        <Route path="login" element={<StoreLogin />} />
        <Route path="register" element={<StoreRegister />} />
        <Route path="account" element={<StoreAccount />} />
        <Route path="forgot-password" element={<StoreForgotPassword />} />
        <Route path="reset-password" element={<StoreResetPassword />} />
        <Route path="order/verify" element={<OrderVerify />} />
        <Route path="checkout/success" element={<CheckoutSuccess />} />
      </Route>

      {/* Dev/preview mount for the Infinity Touch customer site (prod = its own host) */}
      <Route path="/it-customer" element={<InfinityLayout />}>
        {infinityChildRoutes()}
      </Route>

      <Route path="/checkout" element={<Checkout />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/customer/login" element={<CustomerLogin />} />
      <Route path="/customer/register" element={<CustomerRegister />} />
      <Route path="/customer/forgot-password" element={<CustomerForgotPassword />} />
      <Route path="/customer/reset-password" element={<CustomerResetPassword />} />
      <Route path="/customer/account" element={<CustomerAccount />} />
      <Route path="/order/verify" element={<OrderVerify />} />
      <Route path="/guest/order/:orderId" element={<GuestOrderTracking />} />
      <Route path="/pages/:slug" element={<PublicContentPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

import { CartProvider } from './context/CartContext';
import { CustomerProvider } from './context/CustomerContext';
import { PlatformProvider } from './context/PlatformContext';

const IT_SURFACE_SUFFIX = {
  customer: " | Customer Portal",
  distributor: " | Distributor Portal",
  other: "",
};

const setFaviconHref = (href) => {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
};

// Tab title/favicon for infinitytouch.app hosts (customer/distributor/other
// subdomains) come from the same public website-content CMS that already
// drives the Infinity Touch marketing site's own <head> — not a hardcoded
// frontend string — so an admin editing it in /admin/website-content updates
// the SPA's tab branding too. eDisplayroom hosts are untouched: this never
// fires there, so index.html's own <title>/favicon stand as-is.
const applyInfinityTouchBranding = () => {
  const surface = getInfinityTouchSurface();
  if (!surface) return;

  const suffix = IT_SURFACE_SUFFIX[surface];
  axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/website/content`)
    .then(({ data }) => {
      document.title = (data?.seo?.title || "Infinity Touch") + suffix;
      const faviconPath = data?.site?.favicon_url;
      if (faviconPath) {
        setFaviconHref(faviconPath.startsWith('http')
          ? faviconPath
          : `${process.env.REACT_APP_BACKEND_URL}${faviconPath}`);
      }
    })
    .catch(() => {
      // Network hiccup — fall back to a bundled local copy rather than leaving
      // eDisplayroom's title/icon showing on an Infinity Touch domain.
      document.title = "Infinity Touch" + suffix;
      setFaviconHref('/favicon-infinity.ico');
    });
};

function App() {
  useEffect(() => {
    applyInfinityTouchBranding();
  }, []);

  return (
    <BrowserRouter>
      <PlatformProvider>
        <CartProvider>
          <CustomerProvider>
            <InfinityCustomerProvider>
              <div className="App">
                <Toaster position="top-center" />
                <ScrollToTop />
                <AppRouter />
              </div>
            </InfinityCustomerProvider>
          </CustomerProvider>
        </CartProvider>
      </PlatformProvider>
    </BrowserRouter>
  );
}

export default App;
